import {
  IoExpandOutline,
  IoAlbumsOutline,
  IoNewspaperOutline,
  IoMegaphoneOutline,
  IoImageOutline,
  IoBookOutline,
  IoGridOutline,
  IoEllipseOutline,
  IoTrophyOutline,
  IoPlayCircleOutline,
  IoTimeOutline,
  IoAppsOutline,
  IoStarOutline,
  IoFlashOutline,
  IoLayersOutline,
  IoHeartOutline,
  IoRibbonOutline,
  IoRadioOutline,
  IoCalendarOutline,
} from "react-icons/io5";

// The Widgets Library shown in the Experience Builder's left panel is
// backend-driven (GET /api/v1/widget-types) instead of hardcoded here -- see
// HomePageBuilder's fetchWidgetTypes. `value` is what's persisted as
// SectionCategory.layout_type on the backend (a free-form string, no
// schema-level allow-list), so any row the API returns is immediately
// usable, as long as RAIL_CLASS_KEY (below) and the canvas renderer know how
// to preview it -- unknown values fall back to the default rail renderer.

// Icons are stored on the backend as react-icons/io5 export names (strings)
// since JSX can't be persisted to a database; this maps those names back to
// elements for rendering.
export const WIDGET_ICON_MAP = {
  IoExpandOutline: <IoExpandOutline />,
  IoAlbumsOutline: <IoAlbumsOutline />,
  IoNewspaperOutline: <IoNewspaperOutline />,
  IoMegaphoneOutline: <IoMegaphoneOutline />,
  IoImageOutline: <IoImageOutline />,
  IoBookOutline: <IoBookOutline />,
  IoGridOutline: <IoGridOutline />,
  IoEllipseOutline: <IoEllipseOutline />,
  IoTrophyOutline: <IoTrophyOutline />,
  IoPlayCircleOutline: <IoPlayCircleOutline />,
  IoTimeOutline: <IoTimeOutline />,
  IoAppsOutline: <IoAppsOutline />,
  IoStarOutline: <IoStarOutline />,
  IoFlashOutline: <IoFlashOutline />,
  IoLayersOutline: <IoLayersOutline />,
  IoHeartOutline: <IoHeartOutline />,
  IoRibbonOutline: <IoRibbonOutline />,
  IoRadioOutline: <IoRadioOutline />,
  IoCalendarOutline: <IoCalendarOutline />,
};

export const DEFAULT_WIDGET_ICON = "IoAppsOutline";

// Groups a flat list of widget rows (as returned by GET /api/v1/widget-types,
// already ordered by sort_order) into the { heading, items } shape the
// Widgets Library renders, preserving the order headings first appear in.
export function groupWidgetRows(rows) {
  const groups = [];
  const byHeading = new Map();
  for (const row of rows || []) {
    const heading = row.heading || "CUSTOM";
    let group = byHeading.get(heading);
    if (!group) {
      group = { heading, items: [] };
      byHeading.set(heading, group);
      groups.push(group);
    }
    group.items.push({
      id: row.id,
      value: row.value,
      label: row.label,
      description: row.description,
      icon: WIDGET_ICON_MAP[row.icon] || WIDGET_ICON_MAP[DEFAULT_WIDGET_ICON],
    });
  }
  return groups;
}

// Widgets whose canvas preview is a single large banner rather than a rail of
// cards -- everything else falls through to the default card-rail renderer.
// NOTE: duplicated in the backend as Client/SectionController.js's
// CLIENT_CAROUSEL_LAYOUT_TYPES -- keep both lists in sync, they will
// silently drift otherwise.
// "carousel" is the Featured Carousel widget -- the website already renders
// it with the same Banner component as the hero (Home_page/home.js).
export const BANNER_LAYOUT_TYPES = ["carousel", "hero-banner", "editorial-collection", "promotional-banner"];

// Widgets whose content is computed server-side rather than admin-picked.
// NOTE: duplicated in the backend as SectionController.js's
// ALGORITHMIC_DATA_SOURCES -- keep both in sync, they will silently drift
// otherwise.
export const ALGORITHMIC_DATA_SOURCES = ["trending", "continue-watching", "recently-added"];

export const RAIL_CLASS_KEY = {
  carousel: "railCarousel",
  circle: "railCircle",
  square: "railSquare",
  "row-16x9": "railRow16x9",
  "row-2x3": "railRow2x3",
  top10: "railRow2x3",
  "continue-watching": "railRow16x9",
  "recently-added": "railRow16x9",
  "live-channels": "railRow16x9",
  "upcoming-events": "railRow16x9",
};

// Suggests each layout type's natural card shape -- a Circle Avatars row as
// circles, a Square Grid as squares, etc. The Section Inspector's "Image
// ratio" control (cardDesign.imageRatio) always takes priority when set; this
// map is only the fallback for categories saved before that field existed.
export const LAYOUT_ASPECT_RATIO = {
  carousel: "16:9",
  "row-16x9": "16:9",
  "continue-watching": "16:9",
  "recently-added": "16:9",
  "live-channels": "16:9",
  "upcoming-events": "16:9",
  "row-2x3": "2:3",
  top10: "2:3",
  square: "1:1",
  circle: "1:1",
};

export const DEVICE_PRESETS = {
  web: { label: "Web", frameWidth: null, caption: "Web · Responsive", navStyle: "top" },
  tv: { label: "TV", frameWidth: 960, caption: "TV · 1920 × 1080 · 10-foot UI · top nav", navStyle: "tv" },
  mobile: { label: "Mobile", frameWidth: 340, caption: "Mobile · 390 × 844 · bottom tab nav", navStyle: "bottom" },
};

// "manual" is hand-picked content only, added via Browse Media Library.
// "category" pulls its content from the category/genre tags picked in the
// "Add Content via Category" row below the Data Source select -- unlike the
// algorithmic sources it's still admin-driven, not server-computed, so it's
// deliberately left out of ALGORITHMIC_DATA_SOURCES below.
export const DATA_SOURCE_OPTIONS = [
  { value: "manual", label: "Manual Content" },
  { value: "category", label: "Category" },
  { value: "playlist", label: "Playlist" },
  { value: "tags", label: "Tags" },
  { value: "search-filter", label: "Search Filter" },
  { value: "recommendation-engine", label: "Recommendation Engine" },
  { value: "recently-added", label: "Recently Added" },
  { value: "trending", label: "Trending" },
  { value: "most-watched", label: "Most Watched" },
  { value: "ai-collection", label: "AI Collection" },
  { value: "continue-watching", label: "Continue Watching" },
];

// Listed in DATA_SOURCE_OPTIONS above so the picker matches the full design
// spec, but nothing server-side computes their content yet -- picking one
// just falls back to manual/Browse-Media-Library behavior with a note
// telling the admin it isn't wired up yet. Remove an entry here once its
// real fetch logic lands (and add it to ALGORITHMIC_DATA_SOURCES if it ends
// up server-computed).
export const PLACEHOLDER_DATA_SOURCES = [
  "playlist",
  "tags",
  "search-filter",
  "recommendation-engine",
  "most-watched",
  "ai-collection",
];

export const IMAGE_RATIO_OPTIONS = [
  { value: "16:9", label: "16:9 Landscape" },
  { value: "2:3", label: "2:3 Portrait" },
  { value: "1:1", label: "1:1 Square" },
  { value: "4:3", label: "4:3 Classic" },
];
export const TITLE_POSITION_OPTIONS = [
  { value: "below", label: "Below card" },
  { value: "overlay", label: "Overlay" },
  { value: "hidden", label: "Hidden" },
];

export const THEME_ACCENT_PRESETS = ["#B87333", "#E24B4A", "#3E7BFA", "#2FA86A", "#8B5CF6", "#E8B93A"];

// Primary text color for the app (rail titles, card names, active nav).
export const THEME_FONT_COLOR_PRESETS = ["#FFFFFF", "#E5E5E5", "#B1B1B1", "#F5E6D3", "#111111"];

// Extended palette shown in the "+" popover when adding a custom accent
// color or a custom app background. The fixed preset lists above stay the
// quick-pick sets; anything chosen from here (or typed as a hex value) is
// saved server-side (ThemeSetting.custom_accent_colors /
// .custom_backgrounds), so it persists and shows up as an extra swatch from
// then on.
export const THEME_COLOR_PALETTE = [
  "#F87171", "#EF4444", "#DC2626", "#F97316", "#EA580C", "#F59E0B",
  "#EAB308", "#FACC15", "#84CC16", "#65A30D", "#22C55E", "#16A34A",
  "#10B981", "#14B8A6", "#06B6D4", "#0EA5E9", "#3B82F6", "#2563EB",
  "#6366F1", "#7C3AED", "#A855F7", "#C026D3", "#D946EF", "#EC4899",
  "#F43F5E", "#78716C", "#64748B", "#334155", "#111827", "#FFFFFF",
];
export const THEME_BACKGROUND_PRESETS = [
  { value: "charcoal", label: "Charcoal", color: "#141414" },
  { value: "midnight", label: "Midnight", color: "#0D1220" },
  { value: "wine", label: "Wine", color: "#210F14" },
  { value: "forest", label: "Forest", color: "#0F1D14" },
];
export const THEME_FOCUS_EFFECTS = [
  { value: "glow-ring", label: "Glow Ring" },
  { value: "scale", label: "Scale" },
  { value: "lift", label: "Lift" },
];
export const THEME_FONT_OPTIONS = ["Poppins", "Inter", "Roboto", "Montserrat"];
